import type { AccessibilityRole } from 'react-native';
import { Platform } from 'react-native';
import getDistance from 'geolib/es/getDistance';
import type {
  AddressType,
  GooglePlacesAddress,
  PinData,
  Region,
  useUserLocation,
} from '@sweetgreen/garnish';
import { DEFAULT_ZOOM_LEVEL } from '@sweetgreen/garnish';

import type {
  FormatXMLElementFn,
  PrimitiveType,
  TranslationKeys,
} from '../../context';
import type { LocationResultData } from './GraphQL';

// eslint-disable-next-line unicorn/numeric-separators-style
export const METERS_TO_MILES_MULTIPLIER = 0.000621371192;
export const DEFAULT_SEARCH_RADIUS = 25;

export const DEFAULT_BOUNDS = {
  topLeft: { latitude: 0, longitude: 0 },
  bottomRight: { latitude: 0, longitude: 0 },
};
export const DEFAULT_REGION: Required<Region> = {
  lat: 34.0522,
  lng: -118.2437,
  latDelta: 0,
  lngDelta: 0,
  zoom: DEFAULT_ZOOM_LEVEL,
  radius: DEFAULT_SEARCH_RADIUS,
  ...DEFAULT_BOUNDS,
};

export function minimizePins(
  locationSearchType: AddressType,
  locations: readonly PinData[],
): readonly PinData[] {
  return locations.map((location) => {
    const requiredType = location.isOutpost ? 'outpost' : 'pickup';

    return {
      ...location,
      isMinimized: locationSearchType !== requiredType,
    };
  });
}

export function selectActivePin(
  activeId: string,
  locations: readonly PinData[],
): readonly PinData[] {
  if (!activeId) return locations;

  const activeIndex = locations.findIndex(({ id }) => id === activeId);
  const selectedLocation = locations[activeIndex];

  if (!selectedLocation) return locations;

  const unselectedLocationsStart = locations.slice(0, activeIndex);
  const unselectedLocationsEnd = locations.slice(activeIndex + 1);

  // On web, the map library renders the pins based on the order in the list,
  // we want the active pin to be the last one so that it is rendered on top.
  if (Platform.OS === 'web') {
    return [
      ...unselectedLocationsStart,
      ...unselectedLocationsEnd,
      { ...selectedLocation, active: true },
    ] as readonly PinData[];
  }

  // On native, the map library renders the active pin on top by default,
  // and changing the order of the pins causes them to flicker.
  return [
    ...unselectedLocationsStart,
    { ...selectedLocation, active: true },
    ...unselectedLocationsEnd,
  ] as readonly PinData[];
}

export function getUserDistance(
  userLocation: ReturnType<typeof useUserLocation>['geolocation'],
  destination: Region,
): number | undefined {
  const { latitude, longitude } = userLocation ?? {};

  if (!latitude || !longitude) return undefined;
  const distance = getDistance({ lat: latitude, lng: longitude }, destination);

  return distance * METERS_TO_MILES_MULTIPLIER;
}

export function parseAddressIntoInput(address: Partial<GooglePlacesAddress>) {
  return {
    googlePlaceId: address?.googlePlaceId,
    lat: address?.latitude,
    lon: address?.longitude,
    address1: address?.street,
    address2: '',
    city: address?.city,
    state: address?.state,
    zipcode: Number(address?.zipCode),
  };
}

export function getLocationsBySearchType(
  locationSearchType: AddressType,
  locations: ReadonlyArray<PinData & LocationResultData>,
): ReadonlyArray<PinData & LocationResultData> {
  return locations.filter((location) => {
    const requiredType = location.isOutpost ? 'outpost' : 'pickup';

    return locationSearchType === requiredType;
  });
}

export const getLocationA11yProps = (
  t: TranslationType,
  location: LocationResultData,
  role: AccessibilityRole,
) => {
  if (!location) return;

  const { name, address, city, state, zipCode } = location;
  const accessibilityLabel = name;
  const accessibilityHint = t('location.result.a11y-hint', {
    name,
    address,
    city,
    state,
    zipCode,
  });
  const accessibilityRole = role;

  return { accessibilityLabel, accessibilityHint, accessibilityRole };
};

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type TranslationType = (
  key: TranslationKeys,
  values?: Record<string, PrimitiveType | FormatXMLElementFn<string, string>>,
) => string;
